import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import { getComparison, getCurrentUser } from '../services/api';
import InvitePeersModal from './InvitePeersModal';
import '../styles/main.css';

function ComparisonPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const svgRef = useRef();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitationsSent, setInvitationsSent] = useState(false);

  useEffect(() => {
    loadComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadComparison = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Verify user can access this comparison
      const currentUser = getCurrentUser();
      if (!currentUser || currentUser.id !== parseInt(userId)) {
        setError('Access denied');
        setLoading(false);
        return;
      }

      const data = await getComparison(userId);
      
      if (!data.peerAverages) {
        // Check if any invitations were sent
        const hasInvitations = data.stats && data.stats.totalInvited > 0;
        setInvitationsSent(hasInvitations);
        
        if (!hasInvitations) {
          // No invitations sent - show modal to invite peers
          setError('No feedback yet. Start by inviting colleagues to provide feedback.');
          setShowInviteModal(true);
          setLoading(false);
          return;
        }
        
        // Invitations sent but no responses yet
        setError('No peer feedback available yet. Colleagues are still responding to your request.');
        setLoading(false);
        return;
      }

      setComparisonData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load comparison data');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (comparisonData && svgRef.current) {
      drawComparisonChart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonData]);

  const drawComparisonChart = () => {
    const { selfScores, peerAverages } = comparisonData;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    const width = 500;
    const height = 500;
    const margin = 80;
    const radius = Math.min(width, height) / 2 - margin;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Force data
    const forces = [
      { key: 's', label: 'Spark', angle: 0 },
      { key: 'a', label: 'Anchor', angle: Math.PI / 3 },
      { key: 'c', label: 'Connector', angle: 2 * Math.PI / 3 },
      { key: 'g', label: 'Decider', angle: Math.PI },
      { key: 'f', label: 'Builder', angle: 4 * Math.PI / 3 },
      { key: 'p', label: 'Pulse', angle: 5 * Math.PI / 3 }
    ];

    // Draw concentric circles (background)
    const levels = 5;
    for (let i = 1; i <= levels; i++) {
      svg.append('circle')
        .attr('r', (radius / levels) * i)
        .attr('fill', 'none')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);
    }

    // Draw axes
    forces.forEach(force => {
      const x = radius * Math.cos(force.angle - Math.PI / 2);
      const y = radius * Math.sin(force.angle - Math.PI / 2);

      svg.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1);

      // Add labels
      const labelX = (radius + 30) * Math.cos(force.angle - Math.PI / 2);
      const labelY = (radius + 30) * Math.sin(force.angle - Math.PI / 2);

      svg.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', '#374151')
        .text(force.label);
    });

    // Calculate path data
    const getPath = (scores) => {
      const points = forces.map(force => {
        const score = scores[force.key];
        const r = (score / 100) * radius;
        const x = r * Math.cos(force.angle - Math.PI / 2);
        const y = r * Math.sin(force.angle - Math.PI / 2);
        return [x, y];
      });
      
      return 'M' + points.map(p => p.join(',')).join('L') + 'Z';
    };

    // Draw peer data (background layer - filled)
    svg.append('path')
      .datum(peerAverages)
      .attr('d', getPath(peerAverages))
      .attr('fill', '#fca5a5')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 2);

    // Draw self data (foreground layer - filled)
    svg.append('path')
      .datum(selfScores)
      .attr('d', getPath(selfScores))
      .attr('fill', '#bfdbfe')
      .attr('fill-opacity', 0.3)
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2);

    // Add data points for self
    forces.forEach(force => {
      const score = selfScores[force.key];
      const r = (score / 100) * radius;
      const x = r * Math.cos(force.angle - Math.PI / 2);
      const y = r * Math.sin(force.angle - Math.PI / 2);

      svg.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 5)
        .attr('fill', '#3b82f6')
        .attr('stroke', 'white')
        .attr('stroke-width', 2);
    });

    // Add data points for peers
    forces.forEach(force => {
      const score = peerAverages[force.key];
      const r = (score / 100) * radius;
      const x = r * Math.cos(force.angle - Math.PI / 2);
      const y = r * Math.sin(force.angle - Math.PI / 2);

      svg.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 5)
        .attr('fill', '#ef4444')
        .attr('stroke', 'white')
        .attr('stroke-width', 2);
    });
  };

  const calculateDeltas = () => {
    if (!comparisonData) return [];

    const { selfScores, peerAverages } = comparisonData;
    const forceLabels = {
      s: 'Spark',
      a: 'Anchor',
      c: 'Connector',
      g: 'Decider',
      f: 'Builder',
      p: 'Pulse'
    };

    const deltas = Object.keys(selfScores).map(key => ({
      force: forceLabels[key],
      key,
      self: selfScores[key],
      peer: peerAverages[key],
      delta: selfScores[key] - peerAverages[key],
      absDelta: Math.abs(selfScores[key] - peerAverages[key])
    }));

    // Sort by absolute delta
    deltas.sort((a, b) => b.absDelta - a.absDelta);

    return deltas;
  };

  const getHighlights = () => {
    const deltas = calculateDeltas();
    return {
      mostDifferent: deltas.slice(0, 2), // Top 2 biggest gaps
      mostAligned: deltas.slice(-2).reverse() // Bottom 2 smallest gaps
    };
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading comparison...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '2rem' }}>
        <div className="error-message" style={{ maxWidth: '600px', margin: '0 auto' }}>
          {invitationsSent && (
            <button 
              className="btn btn-primary" 
              onClick={() => setShowInviteModal(true)}
              style={{ marginBottom: '1.5rem', width: '100%' }}
            >
              Remind or Request More Feedback
            </button>
          )}
          <h2>Comparison Not Yet Available</h2>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/results')}
            style={{ marginTop: '1rem', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            Back to Results
          </button>
          <InvitePeersModal 
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
          />
        </div>
      </div>
    );
  }

  const deltas = calculateDeltas();
  const highlights = getHighlights();
  const { stats } = comparisonData;

  return (
    <div className="container">
      <div className="comparison-header" style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1.5rem'
        }}>
          <div>
            <h1 style={{ marginBottom: '0.5rem', color: '#0066a1' }}>
              Self vs Others Perception
            </h1>
            <p style={{ color: '#666', fontSize: '1rem' }}>
              Comparing how you see yourself with how {stats.completed} colleague{stats.completed !== 1 ? 's' : ''} see you
            </p>
            <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '0.5rem' }}>
              {stats.completed} of {stats.totalInvited} invited have responded
            </p>
          </div>
          <button 
            onClick={() => setShowInviteModal(true)}
            style={{
              background: '#0066a1',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.3s ease',
              minHeight: 'fit-content'
            }}
            onMouseEnter={(e) => e.target.style.background = '#00528a'}
            onMouseLeave={(e) => e.target.style.background = '#0066a1'}
          >
            Request More Feedback
          </button>
        </div>
      </div>

      <div className="comparison-content" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2rem',
        marginBottom: '2rem'
      }}>
        {/* Chart Section */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.3rem' }}>Perception Overlay</h2>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <svg ref={svgRef}></svg>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '2rem', 
            marginTop: '1.5rem',
            fontSize: '0.9rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: '#bfdbfe',
                border: '2px solid #3b82f6',
                borderRadius: '3px'
              }}></div>
              <span>Your Self-Perception</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: '#fca5a5',
                border: '2px solid #ef4444',
                borderRadius: '3px'
              }}></div>
              <span>Others' Perception</span>
            </div>
          </div>
        </div>

        {/* Highlights Section */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          {/* Most Aligned */}
          <div style={{
            background: '#d1fae5',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '2px solid #10b981'
          }}>
            <h3 style={{ color: '#10b981', marginBottom: '1rem', fontSize: '1.1rem' }}>
              ✓ Most Aligned Forces
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              These are your forces where self-perception matches others' views most closely:
            </p>
            {highlights.mostAligned.map((item, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: idx === 0 ? '0.5rem' : '0'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {item.force}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  You: {item.self} | Others: {item.peer} | Gap: {item.absDelta} points
                </div>
              </div>
            ))}
          </div>

          {/* Most Different */}
          <div style={{
            background: '#fee2e2',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '2px solid #ef4444'
          }}>
            <h3 style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '1.1rem' }}>
              ⚠ Biggest Perception Gaps
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              These forces show the largest difference between how you and others see you:
            </p>
            {highlights.mostDifferent.map((item, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: idx === 0 ? '0.5rem' : '0'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {item.force}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  You: {item.self} | Others: {item.peer} | Gap: {item.absDelta} points
                  {item.delta > 0 ? ' (You rate yourself higher)' : ' (Others rate you higher)'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full Delta Table */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginBottom: '1.5rem', fontSize: '1.3rem' }}>Complete Comparison</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Force</th>
              <th style={{ padding: '0.75rem', textAlign: 'center' }}>Your Score</th>
              <th style={{ padding: '0.75rem', textAlign: 'center' }}>Others' Average</th>
              <th style={{ padding: '0.75rem', textAlign: 'center' }}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {deltas.map((item, idx) => (
              <tr key={idx} style={{
                borderBottom: '1px solid #e5e7eb',
                background: idx % 2 === 0 ? '#f9fafb' : 'white'
              }}>
                <td style={{ padding: '0.75rem', fontWeight: 'bold' }}>{item.force}</td>
                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{item.self}</td>
                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{item.peer}</td>
                <td style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  color: item.delta > 0 ? '#ef4444' : item.delta < 0 ? '#3b82f6' : '#666',
                  fontWeight: 'bold'
                }}>
                  {item.delta > 0 ? '+' : ''}{item.delta}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/results')}
        >
          Back to Results
        </button>
      </div>

      <InvitePeersModal 
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </div>
  );
}

export default ComparisonPage;
